import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  IconButton,
  Container,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import useEvent from "../hooks/useEvent";
import EventManager from "../components/EventManager";
import EventList from "../components/EventList";
import EventForm from "../components/EventForm";
import StaffAssignForm from "../components/StaffAssignForm";
import EquipmentAssignForm from "../components/EquipmentAssignForm";
import ScheduleForm from "../components/ScheduleForm";
import ExpenseForm from "../components/ExpenseForm";
import Loader from "../../../components/common/Loader";
import Modal from "../../../components/common/Modal";

const EventPage = () => {
  const {
    events,
    selectedEvent,
    loading,
    error,
    success,
    filters,
    getEvents,
    getEventById,
    createNewEvent,
    updateExistingEvent,
    deleteExistingEvent,
    assignStaffToEvent,
    assignEquipmentToEvent,
    addScheduleDay,
    addEventExpense,
    updateEventWorkflow,
    clearErrorState,
    clearSuccessState,
    updateFilters,
    clearSelectedEvent,
  } = useEvent();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [loadingAction, setLoadingAction] = useState(false);

  useEffect(() => {
    getEvents(filters);
  }, [filters]);

  const handleSelectEvent = (id) => {
    setSelectedEventId(id);
    getEventById(id);
    setViewMode("details");
  };

  const handleBackToList = () => {
    setSelectedEventId(null);
    clearSelectedEvent();
    setViewMode("list");
  };

  const withLoading = async (fn) => {
    setLoadingAction(true);
    try {
      await fn();
      clearErrorState();
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleCreateEvent = (data) =>
    withLoading(async () => {
      await createNewEvent(data, data.createdBy);
      setShowCreateModal(false);
      await getEvents(filters);
    });

  const handleUpdateEvent = (data) =>
    withLoading(async () => {
      await updateExistingEvent(selectedEventId, data);
      setShowEditModal(false);
      await getEventById(selectedEventId);
    });

  const handleDeleteEvent = (id) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      withLoading(async () => {
        await deleteExistingEvent(id);
        if (selectedEventId === id) handleBackToList();
        await getEvents(filters);
      });
    }
  };

  const handleAssignStaff = (staffMember) =>
    withLoading(async () => {
      await assignStaffToEvent(selectedEventId, staffMember);
      setShowStaffModal(false);
    });

  const handleAssignEquipment = (equipmentItem) =>
    withLoading(async () => {
      await assignEquipmentToEvent(selectedEventId, equipmentItem);
      setShowEquipmentModal(false);
    });

  const handleAddSchedule = (scheduleDay) =>
    withLoading(async () => {
      await addScheduleDay(selectedEventId, scheduleDay);
      setShowScheduleModal(false);
    });

  const handleAddExpense = (expense) =>
    withLoading(async () => {
      await addEventExpense(selectedEventId, expense);
      setShowExpenseModal(false);
    });

  const handleWorkflowUpdate = (workflowUpdates) =>
    withLoading(() => updateEventWorkflow(selectedEventId, workflowUpdates));

  if (loading && !events.length) return <Loader fullScreen />;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Events
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage bookings, staff, equipment & workflow
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setShowCreateModal(true)}
          startIcon={<AddIcon />}
          disabled={loadingAction}
        >
          New Event
        </Button>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <IconButton size="small" onClick={clearErrorState}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          action={
            <IconButton size="small" onClick={clearSuccessState}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {success}
        </Alert>
      )}

      {viewMode === "list" && (
        <EventList
          events={events}
          onSelect={handleSelectEvent}
          onDelete={handleDeleteEvent}
          filters={filters}
          onFilterChange={updateFilters}
          loading={loading}
        />
      )}

      {viewMode === "details" && selectedEvent && (
        <Box>
          <Button
            onClick={handleBackToList}
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 3 }}
          >
            Back to Events
          </Button>
          <EventManager
            event={selectedEvent}
            onEdit={() => setShowEditModal(true)}
            onDelete={() => handleDeleteEvent(selectedEvent.id)}
            onAssignStaff={() => setShowStaffModal(true)}
            onAssignEquipment={() => setShowEquipmentModal(true)}
            onAddSchedule={() => setShowScheduleModal(true)}
            onAddExpense={() => setShowExpenseModal(true)}
            onWorkflowUpdate={handleWorkflowUpdate}
            onRemoveStaff={() => {}}
            onRemoveEquipment={() => {}}
            onMarkEquipmentTaken={() => {}}
            onMarkEquipmentReturned={() => {}}
            onRemoveExpense={() => {}}
          />
        </Box>
      )}

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Event"
      >
        <EventForm
          onSubmit={handleCreateEvent}
          onCancel={() => setShowCreateModal(false)}
          loading={loadingAction}
        />
      </Modal>

      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Event"
      >
        <EventForm
          initialData={selectedEvent}
          onSubmit={handleUpdateEvent}
          onCancel={() => setShowEditModal(false)}
          loading={loadingAction}
        />
      </Modal>

      <Modal
        isOpen={showStaffModal}
        onClose={() => setShowStaffModal(false)}
        title="Assign Staff"
      >
        <StaffAssignForm
          onSubmit={handleAssignStaff}
          onCancel={() => setShowStaffModal(false)}
          loading={loadingAction}
        />
      </Modal>

      <Modal
        isOpen={showEquipmentModal}
        onClose={() => setShowEquipmentModal(false)}
        title="Add Equipment"
      >
        <EquipmentAssignForm
          onSubmit={handleAssignEquipment}
          onCancel={() => setShowEquipmentModal(false)}
          loading={loadingAction}
        />
      </Modal>

      <Modal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        title="Add Shoot Day"
      >
        <ScheduleForm
          availableStaff={selectedEvent?.staff || []}
          onSubmit={handleAddSchedule}
          onCancel={() => setShowScheduleModal(false)}
          loading={loadingAction}
        />
      </Modal>

      <Modal
        isOpen={showExpenseModal}
        onClose={() => setShowExpenseModal(false)}
        title="Add Expense"
      >
        <ExpenseForm
          onSubmit={handleAddExpense}
          onCancel={() => setShowExpenseModal(false)}
          loading={loadingAction}
        />
      </Modal>
    </Container>
  );
};

export default EventPage;
